import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { fetchPublicJson, PublicContactContent } from "./publicApi";
import { formatPhoneLtr } from "../../utils/phoneDisplay";
import "./Public.scss";

const PublicContactUs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const isArabic = searchParams.get("lang") === "ar";
  const [contact, setContact] = useState<PublicContactContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);

    fetchPublicJson<PublicContactContent>("cms/contact")
      .then((data) => {
        setContact(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  const toggleLanguage = () => {
    const next = new URLSearchParams(location.search);
    if (isArabic) {
      next.delete("lang");
    } else {
      next.set("lang", "ar");
    }
    const query = next.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ""}`, { replace: true });
  };

  const phoneDisplay =
    contact?.phone && contact?.dialCode
      ? formatPhoneLtr(contact.dialCode, contact.phone)
      : "";
  const phoneHref =
    contact?.phone && contact?.dialCode
      ? `tel:+${contact.dialCode}${contact.phone}`
      : "";
  const whatsappHref =
    contact?.whatsappPhone && contact?.whatsappDialCode
      ? `https://wa.me/${contact.whatsappDialCode}${contact.whatsappPhone}`
      : "";

  return (
    <main className="public-container property-page" dir={isArabic ? "rtl" : "ltr"}>
      <header className="public-header">
        <a className="public-brand" href="/">
          <img
            className="brand-logo brand-logo--header"
            src="/static/images/brand-logo.png"
            alt="4Homes"
          />
        </a>
        <div className="header-actions">
          <button type="button" className="language-switch" onClick={toggleLanguage}>
            {isArabic ? "English" : "العربية"}
          </button>
        </div>
      </header>

      <div className="legal-shell">
        <article className="legal-card contact-card">
          <h1 className="legal-title">
            {isArabic ? "اتصل بنا" : "Contact Us"}
          </h1>
          <p className="contact-intro">
            {isArabic
              ? "تواصل مع فريق دعم 4Homes عبر القنوات التالية."
              : "Reach the 4Homes support team through the channels below."}
          </p>

          {loading ? (
            <div className="loader">{isArabic ? "جاري التحميل..." : "Loading..."}</div>
          ) : error ? (
            <div className="legal-empty">
              {isArabic ? "تعذر تحميل معلومات الاتصال." : "Unable to load contact information."}
            </div>
          ) : (
            <div className="contact-methods">
              {contact?.email ? (
                <a className="contact-method" href={`mailto:${contact.email}`}>
                  <span className="contact-method-icon">
                    <EmailOutlinedIcon />
                  </span>
                  <span>
                    <small>{isArabic ? "البريد الإلكتروني" : "Email"}</small>
                    <strong>{contact.email}</strong>
                  </span>
                </a>
              ) : null}

              {phoneDisplay ? (
                <a className="contact-method" href={phoneHref}>
                  <span className="contact-method-icon">
                    <PhoneOutlinedIcon />
                  </span>
                  <span>
                    <small>{isArabic ? "الهاتف" : "Phone"}</small>
                    <strong dir="ltr">{phoneDisplay}</strong>
                  </span>
                </a>
              ) : null}

              {whatsappHref ? (
                <a
                  className="contact-method"
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="contact-method-icon whatsapp">
                    <WhatsAppIcon />
                  </span>
                  <span>
                    <small>{isArabic ? "واتساب" : "WhatsApp"}</small>
                    <strong dir="ltr">
                      {formatPhoneLtr(
                        contact?.whatsappDialCode || "",
                        contact?.whatsappPhone || "",
                      )}
                    </strong>
                  </span>
                </a>
              ) : null}

              {!contact?.email && !phoneDisplay && !whatsappHref ? (
                <div className="legal-empty">
                  {isArabic ? "لا توجد بيانات اتصال." : "No contact details available."}
                </div>
              ) : null}
            </div>
          )}

          {contact?.country ? (
            <p className="contact-country">
              {isArabic ? "البلد:" : "Country:"} {contact.country}
            </p>
          ) : null}
        </article>
      </div>
    </main>
  );
};

export default PublicContactUs;
